import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import { DepotBar, LineComparisonChart } from './ControlTowerCharts';
import { ALL_DEPOT_IDS, DEPOT_PROFILES } from './mockData';
import {
  depotById,
  formatCurrency,
  formatCurrencyPer,
  formatHours,
  formatKg,
  formatNumber,
  formatPercent,
  formatShortDate,
  formatTonnes,
  getAggregateSeries,
  getDepotSeries,
  getLatestSnapshot,
  getPercentDelta,
  getPreviousSnapshot,
} from './metrics';
import { DepotDailySnapshot, DepotId, MetricSnapshot } from './types';

const ORG_COLOR = '#3f473a';
const SELECTED_COLOR = '#0f766e';
const CAPACITY_COLOR = '#426b1f';
const COST_COLOR = '#b45309';
const EXCEPTION_COLOR = '#be123c';
const SURFACE = '#f3f6f0';
const INK = '#1f2a20';

type Tone = 'good' | 'bad' | 'neutral';

interface MetricRow {
  group: string;
  label: string;
  selected: number;
  org: number;
  formatter: (value: number) => string;
  lowerIsBetter?: boolean;
}

interface KpiItem {
  label: string;
  value: string;
  delta: number;
  lowerIsBetter?: boolean;
  compare: string;
  accent: string;
  icon: React.ReactNode;
}

const signedPercent = (value: number) => `${value > 0 ? '+' : ''}${formatNumber(value, 1)}%`;

const getTone = (delta: number, lowerIsBetter = false): Tone => {
  if (Math.abs(delta) < 0.2) {
    return 'neutral';
  }
  const favorable = lowerIsBetter ? delta < 0 : delta > 0;
  return favorable ? 'good' : 'bad';
};

const toneColor = (tone: Tone) => {
  switch (tone) {
    case 'good':
      return SELECTED_COLOR;
    case 'bad':
      return EXCEPTION_COLOR;
    default:
      return '#5f6f5a';
  }
};

const compareToOrg = (selected: number, org: number, lowerIsBetter?: boolean) => {
  const delta = getPercentDelta(selected, org);
  if (Math.abs(delta) < 0.2) {
    return 'Org par';
  }
  return `${signedPercent(delta)} vs org${lowerIsBetter ? ' lower better' : ''}`;
};

const getMetricRows = (latestSelected: MetricSnapshot, latestOrg: MetricSnapshot): MetricRow[] => [
  { group: 'Inputs', label: 'Touch points', selected: latestSelected.touchPoints, org: latestOrg.touchPoints, formatter: formatNumber },
  { group: 'Inputs', label: 'Tonnage', selected: latestSelected.tonnageKg, org: latestOrg.tonnageKg, formatter: formatTonnes },
  { group: 'Inputs', label: 'Volume', selected: latestSelected.volume, org: latestOrg.volume, formatter: (value) => `${formatNumber(value)} cu m` },
  { group: 'Inputs', label: 'AWT', selected: latestSelected.awtKg, org: latestOrg.awtKg, formatter: formatKg },
  { group: 'Vehicles', label: 'Vehicles available', selected: latestSelected.vehiclesAvailable, org: latestOrg.vehiclesAvailable, formatter: formatNumber },
  { group: 'Vehicles', label: 'Used vehicle capacity', selected: latestSelected.usedVehicleCapacityKg, org: latestOrg.usedVehicleCapacityKg, formatter: formatTonnes },
  { group: 'Vehicles', label: 'Available capacity', selected: latestSelected.totalAvailableTonnageKg, org: latestOrg.totalAvailableTonnageKg, formatter: formatTonnes },
  { group: 'Vehicles', label: 'Vehicles used', selected: latestSelected.vehiclesUsed, org: latestOrg.vehiclesUsed, formatter: formatNumber },
  { group: 'Routing', label: 'Trips', selected: latestSelected.totalTrips, org: latestOrg.totalTrips, formatter: formatNumber },
  { group: 'Routing', label: 'TP / trip', selected: latestSelected.touchPointsPerTrip, org: latestOrg.touchPointsPerTrip, formatter: (value) => formatNumber(value, 1) },
  { group: 'Routing', label: 'Kg / trip', selected: latestSelected.kgsPerTrip, org: latestOrg.kgsPerTrip, formatter: formatKg },
  { group: 'Routing', label: 'Time / trip', selected: latestSelected.timePerTripHours, org: latestOrg.timePerTripHours, formatter: formatHours, lowerIsBetter: true },
  { group: 'Routing', label: 'Vehicle cap used', selected: latestSelected.vehicleCapacityUtilizationPct, org: latestOrg.vehicleCapacityUtilizationPct, formatter: formatPercent },
  { group: 'Routing', label: 'Slot breaches', selected: latestSelected.slotBreaches, org: latestOrg.slotBreaches, formatter: formatNumber, lowerIsBetter: true },
  { group: 'Cost', label: 'Vehicle cost', selected: latestSelected.totalVehicleCost, org: latestOrg.totalVehicleCost, formatter: formatCurrency, lowerIsBetter: true },
  { group: 'Cost', label: 'Cost / kg', selected: latestSelected.costPerKg, org: latestOrg.costPerKg, formatter: (value) => formatCurrencyPer(value, 'kg'), lowerIsBetter: true },
  { group: 'Cost', label: 'Cost / TP', selected: latestSelected.costPerTouchPoint, org: latestOrg.costPerTouchPoint, formatter: (value) => formatCurrencyPer(value, 'TP'), lowerIsBetter: true },
];

interface DeltaBadgeProps {
  delta: number;
  lowerIsBetter?: boolean;
  label?: string;
}

const DeltaBadge: React.FC<DeltaBadgeProps> = ({ delta, lowerIsBetter, label }) => {
  const tone = getTone(delta, lowerIsBetter);
  const color = toneColor(tone);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 22,
        px: 0.85,
        borderRadius: 1,
        bgcolor: alpha(color, 0.1),
        color,
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
      }}
    >
      {label ?? signedPercent(delta)}
    </Box>
  );
};

interface KpiStripProps {
  items: KpiItem[];
}

const KpiStrip: React.FC<KpiStripProps> = ({ items }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(8, minmax(0, 1fr))' },
      gap: 1.25,
    }}
  >
    {items.map((item) => (
      <Paper
        key={item.label}
        elevation={0}
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: alpha(item.accent, 0.18),
          bgcolor: '#ffffff',
          p: 1.35,
          minHeight: 112,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ color: alpha(INK, 0.64), fontWeight: 950, letterSpacing: 0 }}>
            {item.label}
          </Typography>
          <Box sx={{ color: item.accent, display: 'grid', placeItems: 'center' }}>{item.icon}</Box>
        </Stack>
        <Typography variant="h5" sx={{ color: INK, fontWeight: 950, lineHeight: 1, letterSpacing: 0 }}>
          {item.value}
        </Typography>
        <Stack spacing={0.55} sx={{ mt: 'auto' }}>
          <DeltaBadge delta={item.delta} lowerIsBetter={item.lowerIsBetter} />
          <Typography variant="caption" sx={{ color: alpha(INK, 0.58), fontWeight: 800, lineHeight: 1.15 }}>
            {item.compare}
          </Typography>
        </Stack>
      </Paper>
    ))}
  </Box>
);

interface DepotSelectorProps {
  selectedDepotIds: DepotId[];
  onToggleDepot: (depotId: DepotId) => void;
  onSelectAll: () => void;
}

const DepotSelector: React.FC<DepotSelectorProps> = ({ selectedDepotIds, onToggleDepot, onSelectAll }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const open = Boolean(anchorEl);
  const selectedSet = useMemo(() => new Set(selectedDepotIds), [selectedDepotIds]);
  const allSelected = selectedDepotIds.length === ALL_DEPOT_IDS.length;
  const filteredDepots = DEPOT_PROFILES.filter((depot) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return true;
    }
    return [depot.name, depot.shortName, depot.region, depot.id].some((value) => value.toLowerCase().includes(normalized));
  });
  const selectedNames = selectedDepotIds.map((id) => depotById[id].name);
  const buttonLabel = allSelected
    ? `All depots (${ALL_DEPOT_IDS.length})`
    : selectedDepotIds.length <= 2
      ? selectedNames.join(', ')
      : `${selectedDepotIds.length} depots selected`;

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          borderRadius: 1,
          minHeight: 36,
          minWidth: { xs: '100%', sm: 260 },
          justifyContent: 'space-between',
          textTransform: 'none',
          color: INK,
          borderColor: alpha(INK, 0.2),
          bgcolor: '#ffffff',
          fontWeight: 900,
          '&:hover': { borderColor: SELECTED_COLOR, bgcolor: alpha(SELECTED_COLOR, 0.04) },
        }}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 1,
            mt: 1,
            border: '1px solid',
            borderColor: alpha(INK, 0.12),
            boxShadow: `0 22px 50px ${alpha(INK, 0.18)}`,
          },
        }}
      >
        <Box sx={{ p: 1.25, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search depots"
            value={query}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ color: alpha(INK, 0.62), fontWeight: 850 }}>
              {selectedDepotIds.length} selected
            </Typography>
            <Button size="small" onClick={onSelectAll} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 900 }}>
              Select all
            </Button>
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 330, overflowY: 'auto', py: 0.5 }}>
          {filteredDepots.map((depot) => {
            const selected = selectedSet.has(depot.id);
            const locked = selected && selectedDepotIds.length === 1;
            return (
              <MenuItem
                key={depot.id}
                dense
                disabled={locked}
                onClick={() => onToggleDepot(depot.id)}
                sx={{ gap: 1, minHeight: 42 }}
              >
                <Checkbox checked={selected} size="small" sx={{ p: 0.5, color: depot.color, '&.Mui-checked': { color: depot.color } }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: INK, lineHeight: 1.1 }}>
                    {depot.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(INK, 0.56), lineHeight: 1.1 }}>
                    {depot.region}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: alpha(INK, 0.52), fontWeight: 850 }}>
                  {depot.shortName}
                </Typography>
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};

interface StickyControlsProps {
  selectedDepotIds: DepotId[];
  selectedLabel: string;
  latestDate: string;
  onToggleDepot: (depotId: DepotId) => void;
  onSelectAll: () => void;
}

const StickyControls: React.FC<StickyControlsProps> = ({
  selectedDepotIds,
  selectedLabel,
  latestDate,
  onToggleDepot,
  onSelectAll,
}) => {
  const selectedChips = selectedDepotIds.slice(0, 3);
  const remainingCount = selectedDepotIds.length - selectedChips.length;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 8,
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(INK, 0.12),
        bgcolor: alpha('#ffffff', 0.96),
        backdropFilter: 'blur(10px)',
        px: { xs: 1.5, md: 2 },
        py: 1.15,
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ width: 34, height: 34, borderRadius: 1, bgcolor: alpha(SELECTED_COLOR, 0.1), color: SELECTED_COLOR, display: 'grid', placeItems: 'center' }}>
            <AnalyticsOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ color: INK, fontWeight: 950, letterSpacing: 0, lineHeight: 1 }}>
            Control Tower
          </Typography>
          <Chip size="small" label={`Through ${formatShortDate(latestDate)}`} sx={{ borderRadius: 1, fontWeight: 850 }} />
          <Chip size="small" label="Org baseline" sx={{ borderRadius: 1, fontWeight: 850, bgcolor: alpha(ORG_COLOR, 0.1), color: ORG_COLOR }} />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <DepotSelector selectedDepotIds={selectedDepotIds} onToggleDepot={onToggleDepot} onSelectAll={onSelectAll} />
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ maxWidth: { xs: '100%', lg: 320 } }}>
            {selectedChips.map((depotId) => (
              <Chip
                key={depotId}
                size="small"
                label={depotById[depotId].shortName}
                sx={{ borderRadius: 1, bgcolor: alpha(depotById[depotId].color, 0.12), color: depotById[depotId].color, fontWeight: 900 }}
              />
            ))}
            {remainingCount > 0 ? <Chip size="small" label={`+${remainingCount}`} sx={{ borderRadius: 1, fontWeight: 900 }} /> : null}
            <Typography variant="caption" sx={{ color: alpha(INK, 0.58), fontWeight: 850 }}>
              {selectedLabel}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

interface TrendCardProps {
  title: string;
  latest: string;
  accent: string;
  orgSeries: MetricSnapshot[];
  selectedSeries: MetricSnapshot[];
  metric: (snapshot: MetricSnapshot) => number;
  valueFormatter: (value: number) => string;
  selectedLabel: string;
}

const TrendCard: React.FC<TrendCardProps> = ({
  title,
  latest,
  accent,
  orgSeries,
  selectedSeries,
  metric,
  valueFormatter,
  selectedLabel,
}) => (
  <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: alpha(INK, 0.1), bgcolor: '#ffffff', p: 1.5 }}>
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ color: INK, fontWeight: 950 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 950 }}>
        {latest}
      </Typography>
    </Stack>
    <LineComparisonChart
      orgSeries={orgSeries}
      selectedSeries={selectedSeries}
      metric={metric}
      valueFormatter={valueFormatter}
      selectedColor={accent}
      orgColor={ORG_COLOR}
      selectedLabel={selectedLabel}
      orgLabel="Org"
    />
  </Paper>
);

interface MetricMatrixProps {
  rows: MetricRow[];
}

const MetricMatrix: React.FC<MetricMatrixProps> = ({ rows }) => (
  <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: alpha(INK, 0.1), bgcolor: '#ffffff', overflow: 'hidden' }}>
    <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid', borderColor: alpha(INK, 0.08) }}>
      <Typography variant="body2" sx={{ color: INK, fontWeight: 950 }}>
        Metric matrix
      </Typography>
    </Box>
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 900, display: 'grid', gridTemplateColumns: '120px 1.35fr 1fr 1fr 1fr' }}>
        {['Group', 'Metric', 'Selected', 'Org', 'Signal'].map((label) => (
          <Box key={label} sx={{ px: 1.5, py: 1, bgcolor: alpha(INK, 0.04), color: alpha(INK, 0.62), fontSize: 12, fontWeight: 950 }}>
            {label}
          </Box>
        ))}
        {rows.map((row) => {
          const delta = getPercentDelta(row.selected, row.org);
          return (
            <React.Fragment key={`${row.group}-${row.label}`}>
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: alpha(INK, 0.58), fontWeight: 900, fontSize: 12 }}>
                {row.group}
              </Box>
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: INK, fontWeight: 850, fontSize: 13 }}>
                {row.label}
              </Box>
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: SELECTED_COLOR, fontWeight: 950, fontSize: 13 }}>
                {row.formatter(row.selected)}
              </Box>
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: alpha(INK, 0.68), fontWeight: 850, fontSize: 13 }}>
                {row.formatter(row.org)}
              </Box>
              <Box sx={{ px: 1.5, py: 0.85, borderTop: '1px solid', borderColor: alpha(INK, 0.06) }}>
                <DeltaBadge delta={delta} lowerIsBetter={row.lowerIsBetter} label={compareToOrg(row.selected, row.org, row.lowerIsBetter)} />
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  </Paper>
);

interface DepotComparisonProps {
  depotSnapshots: DepotDailySnapshot[];
}

const DepotComparison: React.FC<DepotComparisonProps> = ({ depotSnapshots }) => {
  const sortedByThroughput = [...depotSnapshots].sort((left, right) => right.touchPoints - left.touchPoints);
  const maxThroughput = Math.max(...sortedByThroughput.map((snapshot) => snapshot.touchPoints), 1);

  return (
    <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: alpha(INK, 0.1), bgcolor: '#ffffff', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid', borderColor: alpha(INK, 0.08) }}>
        <Typography variant="body2" sx={{ color: INK, fontWeight: 950 }}>
          Depot comparison
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '0.95fr 1.05fr' }, gap: 0 }}>
        <Box sx={{ p: 1.5, borderRight: { xl: '1px solid' }, borderColor: alpha(INK, 0.08), maxHeight: 410, overflowY: 'auto' }}>
          <Stack spacing={1.15}>
            {sortedByThroughput.map((snapshot) => {
              const depot = depotById[snapshot.depotId];
              return (
                <DepotBar
                  key={snapshot.depotId}
                  label={depot.name}
                  detail={`${formatTonnes(snapshot.tonnageKg)} | ${formatPercent(snapshot.vehicleCapacityUtilizationPct)} cap`}
                  value={snapshot.touchPoints}
                  max={maxThroughput}
                  color={depot.color}
                  valueLabel={`${formatNumber(snapshot.touchPoints)} TP`}
                />
              );
            })}
          </Stack>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 720, display: 'grid', gridTemplateColumns: '1.15fr repeat(6, 1fr)' }}>
            {['Depot', 'TP', 'Tonnage', 'Cap used', 'Cost/kg', 'Breaches', 'Trips'].map((label) => (
              <Box key={label} sx={{ px: 1.25, py: 1, bgcolor: alpha(INK, 0.04), color: alpha(INK, 0.62), fontSize: 12, fontWeight: 950 }}>
                {label}
              </Box>
            ))}
            {sortedByThroughput.map((snapshot) => {
              const depot = depotById[snapshot.depotId];
              return (
                <React.Fragment key={snapshot.depotId}>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: INK, fontWeight: 950, fontSize: 13 }}>
                    {depot.name}
                  </Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatNumber(snapshot.touchPoints)}</Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatTonnes(snapshot.tonnageKg)}</Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: CAPACITY_COLOR, fontWeight: 950, fontSize: 13 }}>{formatPercent(snapshot.vehicleCapacityUtilizationPct)}</Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatCurrencyPer(snapshot.costPerKg, 'kg')}</Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: snapshot.slotBreaches > 4 ? EXCEPTION_COLOR : INK, fontWeight: snapshot.slotBreaches > 4 ? 950 : 700, fontSize: 13 }}>{formatNumber(snapshot.slotBreaches)}</Box>
                  <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatNumber(snapshot.totalTrips)}</Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

interface DayWiseTableProps {
  selectedSeries: MetricSnapshot[];
  orgSeries: MetricSnapshot[];
}

const DayWiseTable: React.FC<DayWiseTableProps> = ({ selectedSeries, orgSeries }) => (
  <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: alpha(INK, 0.1), bgcolor: '#ffffff', overflow: 'hidden' }}>
    <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid', borderColor: alpha(INK, 0.08) }}>
      <Typography variant="body2" sx={{ color: INK, fontWeight: 950 }}>
        Day-wise ledger
      </Typography>
    </Box>
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 980, display: 'grid', gridTemplateColumns: '108px repeat(9, minmax(96px, 1fr))' }}>
        {['Day', 'TP', 'Tonnage', 'Trips', 'Cap used', 'Breaches', 'Cost/kg', 'Cost/TP', 'Org cap', 'Org cost/kg'].map((label) => (
          <Box key={label} sx={{ px: 1.25, py: 1, bgcolor: alpha(INK, 0.04), color: alpha(INK, 0.62), fontSize: 12, fontWeight: 950 }}>
            {label}
          </Box>
        ))}
        {selectedSeries.map((snapshot, index) => {
          const org = orgSeries[index];
          return (
            <React.Fragment key={snapshot.date}>
              <Box sx={{ px: 1.25, py: 1, color: INK, fontWeight: 950, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>
                {formatShortDate(snapshot.date)}
              </Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatNumber(snapshot.touchPoints)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatTonnes(snapshot.tonnageKg)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatNumber(snapshot.totalTrips)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: CAPACITY_COLOR, fontWeight: 950, fontSize: 13 }}>{formatPercent(snapshot.vehicleCapacityUtilizationPct)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: snapshot.slotBreaches > org.slotBreaches ? EXCEPTION_COLOR : INK, fontSize: 13 }}>{formatNumber(snapshot.slotBreaches)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatCurrencyPer(snapshot.costPerKg, 'kg')}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), fontSize: 13 }}>{formatCurrencyPer(snapshot.costPerTouchPoint, 'TP')}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: alpha(INK, 0.64), fontSize: 13 }}>{formatPercent(org.vehicleCapacityUtilizationPct)}</Box>
              <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid', borderColor: alpha(INK, 0.06), color: alpha(INK, 0.64), fontSize: 13 }}>{formatCurrencyPer(org.costPerKg, 'kg')}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  </Paper>
);

const ControlTower: React.FC = () => {
  const [selectedDepotIds, setSelectedDepotIds] = useState<DepotId[]>(ALL_DEPOT_IDS);

  const orgSeries = useMemo(() => getAggregateSeries(ALL_DEPOT_IDS), []);
  const selectedSeries = useMemo(() => getAggregateSeries(selectedDepotIds), [selectedDepotIds]);
  const latestOrg = getLatestSnapshot(orgSeries);
  const latestSelected = getLatestSnapshot(selectedSeries);
  const previousSelected = getPreviousSnapshot(selectedSeries);
  const selectedDepotSnapshots = useMemo(() => selectedDepotIds.map((depotId) => getLatestSnapshot(getDepotSeries(depotId))), [selectedDepotIds]);
  const metricRows = useMemo(() => getMetricRows(latestSelected, latestOrg), [latestOrg, latestSelected]);

  const selectedLabel = selectedDepotIds.length === ALL_DEPOT_IDS.length ? 'All depots' : `${selectedDepotIds.length} selected`;
  const kpiItems: KpiItem[] = [
    {
      label: 'Touch points',
      value: formatNumber(latestSelected.touchPoints),
      delta: getPercentDelta(latestSelected.touchPoints, previousSelected?.touchPoints ?? null),
      compare: compareToOrg(latestSelected.touchPoints, latestOrg.touchPoints),
      accent: SELECTED_COLOR,
      icon: <AnalyticsOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Tonnage',
      value: formatTonnes(latestSelected.tonnageKg),
      delta: getPercentDelta(latestSelected.tonnageKg, previousSelected?.tonnageKg ?? null),
      compare: compareToOrg(latestSelected.tonnageKg, latestOrg.tonnageKg),
      accent: SELECTED_COLOR,
      icon: <RouteOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Vehicle cap used',
      value: formatPercent(latestSelected.vehicleCapacityUtilizationPct),
      delta: getPercentDelta(latestSelected.vehicleCapacityUtilizationPct, previousSelected?.vehicleCapacityUtilizationPct ?? null),
      compare: `${formatTonnes(latestSelected.tonnageKg)} / ${formatTonnes(latestSelected.usedVehicleCapacityKg)}`,
      accent: CAPACITY_COLOR,
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Trips',
      value: formatNumber(latestSelected.totalTrips),
      delta: getPercentDelta(latestSelected.totalTrips, previousSelected?.totalTrips ?? null),
      compare: `${formatNumber(latestSelected.touchPointsPerTrip, 1)} TP/trip`,
      accent: COST_COLOR,
      icon: <RouteOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Cost / kg',
      value: formatCurrencyPer(latestSelected.costPerKg, 'kg'),
      delta: getPercentDelta(latestSelected.costPerKg, previousSelected?.costPerKg ?? null),
      lowerIsBetter: true,
      compare: compareToOrg(latestSelected.costPerKg, latestOrg.costPerKg, true),
      accent: COST_COLOR,
      icon: <TollOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Cost / TP',
      value: formatCurrencyPer(latestSelected.costPerTouchPoint, 'TP'),
      delta: getPercentDelta(latestSelected.costPerTouchPoint, previousSelected?.costPerTouchPoint ?? null),
      lowerIsBetter: true,
      compare: compareToOrg(latestSelected.costPerTouchPoint, latestOrg.costPerTouchPoint, true),
      accent: COST_COLOR,
      icon: <TollOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Breaches',
      value: formatNumber(latestSelected.slotBreaches),
      delta: getPercentDelta(latestSelected.slotBreaches, previousSelected?.slotBreaches ?? null),
      lowerIsBetter: true,
      compare: compareToOrg(latestSelected.slotBreaches, latestOrg.slotBreaches, true),
      accent: EXCEPTION_COLOR,
      icon: <ReportProblemOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Time / trip',
      value: formatHours(latestSelected.timePerTripHours),
      delta: getPercentDelta(latestSelected.timePerTripHours, previousSelected?.timePerTripHours ?? null),
      lowerIsBetter: true,
      compare: compareToOrg(latestSelected.timePerTripHours, latestOrg.timePerTripHours, true),
      accent: EXCEPTION_COLOR,
      icon: <RouteOutlinedIcon fontSize="small" />,
    },
  ];

  const toggleDepot = (depotId: DepotId) => {
    setSelectedDepotIds((current) => {
      if (current.includes(depotId)) {
        return current.length === 1 ? current : current.filter((id) => id !== depotId);
      }
      return [...current, depotId];
    });
  };

  return (
    <Box sx={{ minHeight: '100%', bgcolor: SURFACE, color: INK, fontFamily: '"Aptos", "Segoe UI", "Helvetica Neue", sans-serif' }}>
      <Box sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1.5 }}>
        <StickyControls
          selectedDepotIds={selectedDepotIds}
          selectedLabel={selectedLabel}
          latestDate={latestSelected.date}
          onToggleDepot={toggleDepot}
          onSelectAll={() => setSelectedDepotIds(ALL_DEPOT_IDS)}
        />

        <KpiStrip items={kpiItems} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          <TrendCard
            title="Day-wise capacity utilization"
            latest={formatPercent(latestSelected.vehicleCapacityUtilizationPct)}
            accent={CAPACITY_COLOR}
            orgSeries={orgSeries}
            selectedSeries={selectedSeries}
            metric={(snapshot) => snapshot.vehicleCapacityUtilizationPct}
            valueFormatter={formatPercent}
            selectedLabel={selectedLabel}
          />
          <TrendCard
            title="Day-wise cost / kg"
            latest={formatCurrencyPer(latestSelected.costPerKg, 'kg')}
            accent={COST_COLOR}
            orgSeries={orgSeries}
            selectedSeries={selectedSeries}
            metric={(snapshot) => snapshot.costPerKg}
            valueFormatter={(value) => formatCurrencyPer(value, 'kg')}
            selectedLabel={selectedLabel}
          />
          <TrendCard
            title="Day-wise cost / TP"
            latest={formatCurrencyPer(latestSelected.costPerTouchPoint, 'TP')}
            accent={EXCEPTION_COLOR}
            orgSeries={orgSeries}
            selectedSeries={selectedSeries}
            metric={(snapshot) => snapshot.costPerTouchPoint}
            valueFormatter={(value) => formatCurrencyPer(value, 'TP')}
            selectedLabel={selectedLabel}
          />
          <TrendCard
            title="Day-wise slot breaches"
            latest={formatNumber(latestSelected.slotBreaches)}
            accent={EXCEPTION_COLOR}
            orgSeries={orgSeries}
            selectedSeries={selectedSeries}
            metric={(snapshot) => snapshot.slotBreaches}
            valueFormatter={formatNumber}
            selectedLabel={selectedLabel}
          />
        </Box>

        <DepotComparison depotSnapshots={selectedDepotSnapshots} />
        <MetricMatrix rows={metricRows} />
        <DayWiseTable selectedSeries={selectedSeries} orgSeries={orgSeries} />
      </Box>
    </Box>
  );
};

export default ControlTower;

